import { Button } from '@ai-tutor/ui';
import { ExternalLink, Video, PlayCircle } from 'lucide-react';
import type { Source } from '@/data/fixtures';
import { useSourceFileUrl } from '@/hooks/lesson/useSources';
import {
  ViewerLoading,
  ViewerError,
} from '@/components/features/learning/sources/viewers/shell/ViewerStates';
import { ViewerShell } from '@/components/features/learning/sources/viewers/shell/ViewerShell';

interface VideoViewerProps {
  source: Source;
  onOpenExternal: (url: string) => void;
}

/**
 * Two paths:
 *  - **Uploaded video file** (`source.id` resolves a presigned URL) →
 *    native `<video controls>` with the presigned URL as `src`.
 *  - **Link source pointing at a video** (e.g. YouTube): fall back to
 *    "Open in new tab" since most platforms block iframe embedding.
 */
export function VideoViewer({ source, onOpenExternal }: VideoViewerProps) {
  // Only fetch a presigned URL if the source looks like an uploaded
  // file. Link-type sources don't have a `file_key` and the request
  // would 400.
  const isLinkSource = source.type === 'link' || (!!source.url && !source.id);
  const { data, isLoading, error } = useSourceFileUrl(
    isLinkSource ? '' : source.id
  );

  if (isLinkSource) {
    return (
      <div className='flex h-full items-center justify-center p-6'>
        <div className='w-full max-w-md rounded-2xl border border-border/60 p-8 text-center'>
          <div className='mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-notes/10'>
            <PlayCircle className='h-7 w-7 text-notes' />
          </div>
          <h3 className='mb-1 text-lg font-semibold text-foreground'>
            {source.name}
          </h3>
          <p className='mx-auto mb-6 max-w-sm text-sm text-muted-foreground'>
            Most video platforms block embedding in iframes. Open the original
            link in a new tab to watch.
          </p>
          {source.url && (
            <Button onClick={() => onOpenExternal(source.url!)}>
              <ExternalLink className='mr-2 h-4 w-4' />
              Open in new tab
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (isLoading) return <ViewerLoading />;
  if (error || !data?.url)
    return (
      <ViewerError
        message="Couldn't load video"
        detail={(error as Error | undefined)?.message}
      />
    );

  return (
    <ViewerShell
      sourceId={source.id}
      fullscreen
      icon={Video}
      label='Video'
      tone='notes'
    >
      <div className='flex h-full items-center justify-center p-6'>
        <video
          controls
          preload='metadata'
          src={data.url}
          className='max-h-full w-full max-w-4xl rounded-xl bg-black object-contain shadow-xl ring-1 ring-border'
        >
          Your browser doesn't support inline video playback.
        </video>
      </div>
    </ViewerShell>
  );
}
